import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listProducts } from '../services/productsService';
import { aumentarStock, reducirStock } from '../services/stockService';
import { formatMoney } from '../lib/format';
import { friendlyError } from '../lib/errors';
import DataTable from '../components/ui/DataTable';
import SearchInput from '../components/ui/SearchInput';
import Pagination from '../components/ui/Pagination';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';
import ConfirmDialog from '../components/ui/ConfirmDialog';

export default function InventoryPage() {
  const { ruc, user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [stockBajoOnly, setStockBajoOnly] = useState(false);
  const [adjust, setAdjust] = useState(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const canManage = user?.rol !== 'prueba';

  const fetchPage = useCallback(
    async ({ reset = false } = {}) => {
      if (!ruc) return;
      if (reset) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const result = await listProducts({
          ruc,
          search,
          stockBajo: stockBajoOnly,
          cursor: reset ? null : cursor,
        });
        setItems((prev) => (reset ? result.items : [...prev, ...result.items]));
        setCursor(result.cursor || null);
        setHasMore(!!result.hasMore);
      } catch (e) {
        setError(friendlyError(e));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [ruc, search, stockBajoOnly, cursor]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setItems([]);
      setCursor(null);
      fetchPage({ reset: true });
    }, search ? 280 : 0);
    return () => clearTimeout(t);
  }, [ruc, search, stockBajoOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const onAdjust = async () => {
    if (!adjust || !amount) return;
    const qty = Number(amount);
    if (!qty || qty <= 0) {
      setError('Ingresa una cantidad positiva.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (adjust.mode === 'in') {
        await aumentarStock({
          productoId: adjust.uid,
          cantidad: qty,
          tipo: 'compra',
          ruc,
        });
      } else {
        await reducirStock({
          productoId: adjust.uid,
          cantidad: qty,
          tipo: 'ajuste',
          ruc,
        });
      }
      setAdjust(null);
      setAmount('');
      await fetchPage({ reset: true });
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: 'id', label: 'Código', render: (r) => r.id || '—' },
    { key: 'name', label: 'Producto' },
    {
      key: 'stock',
      label: 'Stock',
      render: (r) => {
        const stock = Number(r.stock || 0);
        const min = Number(r.stockMinimo || 0);
        const low = r.controlaStock && stock <= min;
        return (
          <span>
            {stock}
            {low ? <Badge tone="warn"> bajo</Badge> : null}
          </span>
        );
      },
    },
    { key: 'stockMinimo', label: 'Mín.', render: (r) => r.stockMinimo ?? '—' },
    { key: 'controlaLotes', label: 'Lotes', render: (r) => (r.controlaLotes ? 'Sí' : 'No') },
    { key: 'price', label: 'Precio', render: (r) => formatMoney(r.price, r.currency) },
    {
      key: 'actions',
      label: 'Acciones',
      render: (r) =>
        canManage && r.controlaStock ? (
          <div className="row-actions">
            <button type="button" className="btn btn-mini" onClick={() => { setAdjust({ mode: 'in', uid: r.uid, name: r.name }); setAmount(''); }}>
              +
            </button>
            <button type="button" className="btn btn-mini btn-ghost" onClick={() => { setAdjust({ mode: 'out', uid: r.uid, name: r.name }); setAmount(''); }}>
              −
            </button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Inventario</h1>
          <p className="muted">Stock y lotes · FEFO en descontar · misma lógica que la app móvil</p>
        </div>
      </header>

      <div className="toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar producto…" />
        <label className="checkbox">
          <input type="checkbox" checked={stockBajoOnly} onChange={(e) => setStockBajoOnly(e.target.checked)} />
          Solo stock bajo
        </label>
      </div>

      {loading ? (
        <LoadingState label="Cargando inventario…" />
      ) : error && !items.length ? (
        <ErrorState message={error} onRetry={() => fetchPage({ reset: true })} />
      ) : (
        <>
          {error ? <div className="alert error">{error}</div> : null}
          <DataTable
            columns={columns}
            rows={items}
            keyField="uid"
            onRowClick={(row) => navigate(`/admin/productos/${row.uid}`)}
            empty={<EmptyState title="Sin productos en inventario" />}
          />
          <Pagination hasMore={hasMore} onMore={() => fetchPage()} loading={loadingMore} shown={items.length} />
        </>
      )}

      <ConfirmDialog
        open={!!adjust}
        title={adjust?.mode === 'in' ? 'Aumentar stock' : 'Reducir stock'}
        message={
          <span>
            Producto: <strong>{adjust?.name}</strong>
            <br />
            <label className="dialog-field">
              Cantidad
              <input
                type="number"
                min="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                autoFocus
              />
            </label>
          </span>
        }
        confirmLabel={adjust?.mode === 'in' ? 'Aumentar' : 'Reducir'}
        onConfirm={onAdjust}
        onCancel={() => { setAdjust(null); setAmount(''); }}
        loading={busy}
      />
    </div>
  );
}
